import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";
import MainLayout from "./components/layout/MainLayout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Patients from "./pages/Patients";
import Visits from "./pages/Visits";
import Services from "./pages/Services";
import Inventory from "./pages/Inventory";
import Batches from "./pages/Batches";
import Billing from "./pages/Billing";
import Locations from "./pages/Locations";
import Users from "./pages/Users";
import Employees from "./pages/Employees";
import Reports from "./pages/Reports";
import PasswordReset from "./pages/PasswordReset";
import Clinics from "./pages/Clinics";
import Settings from "./pages/Settings";
import LoginAudit from "./pages/LoginAudit";
import MobileInventory from "./pages/MobileInventory";
import BatchImport from "./pages/BatchImport";
import JotFormPage from "./pages/JotFormPage";
import PatientIntakeSubmissions from "./pages/PatientIntakeSubmissions";
import PatientIntake from "./pages/PatientIntake";

// Wrapper component to check for password reset requirement
const PasswordResetGuard = ({ children }) => {
  const { mustResetPassword, user, loading } = useAuth();
  
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }
  
  if (user && mustResetPassword) {
    return <PasswordReset />;
  }
  
  return children;
};

// Smart redirect based on user role
const RoleBasedRedirect = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Redirect based on role
  if (user.role === 'inventory_manager') {
    return <Navigate to="/mobile-inventory" replace />;
  }
  if (user.role === 'doctor') {
    return <Navigate to="/visits" replace />;
  }
  
  return <Navigate to="/dashboard" replace />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/patient-intake" element={<PatientIntake />} />
          <Route path="/" element={
            <PasswordResetGuard>
              <MainLayout />
            </PasswordResetGuard>
          }>
            <Route index element={<RoleBasedRedirect />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="patients" element={<Patients />} />
            <Route path="visits" element={<Visits />} />
            <Route path="services" element={<Services />} />
            <Route path="inventory" element={<Inventory />} />
            <Route path="inventory/:itemId/batches" element={<Batches />} />
            <Route path="inventory/batch-import" element={<BatchImport />} />
            <Route path="billing" element={<Billing />} />
            <Route path="locations" element={<Locations />} />
            <Route path="users" element={<Users />} />
            <Route path="employees" element={<Employees />} />
            <Route path="reports" element={<Reports />} />
            <Route path="jotform" element={<JotFormPage />} />
            <Route path="patient-intake-submissions" element={<PatientIntakeSubmissions />} />
            <Route path="clinics" element={<Clinics />} />
            <Route path="settings" element={<Settings />} />
            <Route path="login-audit" element={<LoginAudit />} />
          </Route>
          {/* Mobile Inventory - standalone route without MainLayout */}
          <Route path="/mobile-inventory" element={
            <PasswordResetGuard>
              <MobileInventory />
            </PasswordResetGuard>
          } />
          <Route path="*" element={<RoleBasedRedirect />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors closeButton />
    </AuthProvider>
  );
}

export default App;
